import React, { useState, useEffect, useRef, useCallback } from 'react';
import Card from './Card';
import Slot from './Slot';
import InvisibleSlot from './InvisibleSlot';
import CardQueue from './CardQueue';
import { CardData, QueueCard, GameState, SlotCard } from '../types/types';
import '../styles/GameBoard.css';

// Import extracted utility functions
import { updateCardVisibility } from '../utils/cardVisibility';
import { checkForMatches } from '../utils/matchingLogic';
import { 
  startNewGame, 
  handleCardClick, 
  handleQueueCardClick, 
  handleRevive,
  handleRevert,
  handleReshuffle,
  handleMoveOut,
  MAX_SLOTS
} from '../utils/gameHandlers';

const GameBoard: React.FC = () => {
  const [gameState, setGameState] = useState<GameState>(startNewGame());
  
  // Add a state to track the invisible slot cards
  const [invisibleSlot, setInvisibleSlot] = useState<SlotCard[]>([]);
  
  // Add a state to track animations in progress
  const [animationInProgress, setAnimationInProgress] = useState<boolean>(false);
  
  // Add a state to track if we're currently processing an invisible card click
  const [processingInvisibleCard, setProcessingInvisibleCard] = useState<boolean>(false);
  
  // Refs for positioning
  const slotsRef = useRef<HTMLDivElement>(null);
  const invisibleSlotsRef = useRef<HTMLDivElement>(null);
  const cardsContainerRef = useRef<HTMLDivElement>(null);

  // Initialize the game
  useEffect(() => {
    startNewGame();
  }, []);

  // Check if all cards have been selected (game win condition)
  useEffect(() => {
    // Win condition: all board cards are selected/cleared
    const allBoardCardsSelected = gameState.cards.length > 0 && 
                                 gameState.cards.every(card => card.selected);
    
    // If all cards on the board are cleared, player wins
    if (allBoardCardsSelected) {
      console.log('Game won! All board cards have been successfully cleared.');
      
      // Short delay before showing victory screen for better UX
      setTimeout(() => {
        setGameState(prevState => ({
          ...prevState,
          isGameWon: true
        }));
      }, 500);
    }
  }, [gameState.cards]);

  // Update card visibility after each move
  useEffect(() => {
    if (gameState.cards.length > 0) {
      const hasVisibilityChanged = updateCardVisibility(gameState.cards);
      if (hasVisibilityChanged) {
        // Only update state if visibility actually changed for any card
        const updatedCards = [...gameState.cards];
        console.log('Updated card visibility, visible cards:', updatedCards.filter(card => card.visible).length);
        
        setGameState(prevState => ({
          ...prevState,
          cards: updatedCards
        }));
      }
    }
  }, [gameState.cards]);

  // Check and handle matches in the slots
  useEffect(() => {
    if (gameState.slots.length >= 3) {
      console.log('Checking for matches in useEffect - slots length:', gameState.slots.length);
      
      // Give the DOM a chance to update before checking for matches
      const checkTimer = setTimeout(() => {
        // Run checkForMatches and if matches were found, wait a bit longer before checking again
        const matchFound = checkForMatches(gameState, setAnimationInProgress, setGameState, animationInProgress);
        
        if (matchFound) {
          // Wait for animation and state update to complete before checking for more matches
          const recursiveCheckTimer = setTimeout(() => {
            if (gameState.slots.length >= 3) {
              checkForMatches(gameState, setAnimationInProgress, setGameState, animationInProgress);
            }
          }, 500); // Animation duration + small buffer
          
          return () => clearTimeout(recursiveCheckTimer);
        }
      }, 50); // Small delay to ensure DOM is updated
      
      return () => clearTimeout(checkTimer);
    }
  // Use slots length as dependency, since we're checking the actual contents in the function
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameState.slots.length, animationInProgress]);

  // Handler for invisible slot card clicks
  const handleInvisibleCardClick = useCallback((clickedCard: SlotCard) => {
    // Prevent multiple rapid clicks
    if (processingInvisibleCard) {
      console.log('Already processing a card, ignoring click');
      return;
    }
    
    // Handle the invisible slot card click by moving it to the main slot
    if (gameState.slots.length < MAX_SLOTS) {
      console.log('Processing invisible card click:', clickedCard.id);
      
      // Set processing state to true to prevent double-clicks
      setProcessingInvisibleCard(true);
      
      // Create a copy of the card to move to the main slot
      const cardToMove = { 
        id: clickedCard.id, 
        type: clickedCard.type 
      };
      
      // First add the card to the main slot
      setGameState(prevState => ({
        ...prevState,
        slots: [...prevState.slots, cardToMove]
      }));
      
      // Wait for animation in InvisibleSlot to complete, then update state
      setTimeout(() => {
        // Simply remove the clicked card from invisible slot, no repositioning
        const updatedInvisibleSlot = invisibleSlot.filter(card => card.id !== clickedCard.id);
        
        // Update the state with the new array
        setInvisibleSlot(updatedInvisibleSlot);
        
        // If all invisible cards are gone, hide the area
        if (updatedInvisibleSlot.length === 0) {
          if (invisibleSlotsRef.current) {
            invisibleSlotsRef.current.style.opacity = '0';
          }
        }
        
        // Reset processing state
        setProcessingInvisibleCard(false);
      }, 300);
    } else {
      // Slots are full, show game over
      setGameState(prevState => ({
        ...prevState,
        isGameOver: true
      }));
    }
  }, [gameState.slots, invisibleSlot, processingInvisibleCard]);

  return (
    <div className="game-board">
      <div className="game-header">
        <div style={{ width: '80px' }}></div> {/* Spacer to balance the restart button */}
        <h1></h1>
        <button className="restart-button" onClick={() => {
          setGameState(startNewGame());
          // Reset invisible slot
          setInvisibleSlot([]);
          if (invisibleSlotsRef.current) {
            invisibleSlotsRef.current.style.opacity = '0';
          }
        }}>重新开始</button>
      </div>

      <div className="game-area">
        <CardQueue 
          cards={gameState.leftQueue} 
          position="left" 
          onCardClick={(card) => handleQueueCardClick(card, 'left', gameState, setGameState, slotsRef)} 
        />
        
        <div className="cards-container" ref={cardsContainerRef}>
          {gameState.cards.map(card => (
            <Card 
              key={card.id} 
              data={card} 
              onClick={(card: CardData) => handleCardClick(card, gameState, setGameState, slotsRef)} 
            />
          ))}
        </div>
        
        <CardQueue 
          cards={gameState.rightQueue} 
          position="right" 
          onCardClick={(card: QueueCard) => handleQueueCardClick(card, 'right', gameState, setGameState, slotsRef)} 
        />
      </div>

      {/* Wrap both slot areas in a container */}
      <div className="slots-wrapper">
        {/* Invisible card slot with 3 positions */}
        <div className="invisible-slot-area" ref={invisibleSlotsRef}>
          <InvisibleSlot 
            cards={invisibleSlot} 
            onCardClick={handleInvisibleCardClick}
          />
        </div>

        <div className="slot-area" ref={slotsRef}>
          <Slot cards={gameState.slots} />
          
          <div className="action-buttons-container">
            {/* Move Out button */}
            <button 
              className={`move-out-button ${gameState.usedMoveOut || gameState.slots.length < 3 ? 'disabled' : ''}`} 
              onClick={() => handleMoveOut(gameState, setGameState, invisibleSlotsRef, setInvisibleSlot)}
              disabled={gameState.usedMoveOut || gameState.slots.length < 3}
              aria-label="Move out cards"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 18l6-6-6-6"></path>
                <path d="M3 6h16v12H3z"></path>
              </svg>
            </button>
            
            {/* Revert button */}
            <button 
              className={`revert-button ${!gameState.canRevert || gameState.usedRevert ? 'disabled' : ''}`} 
              onClick={() => handleRevert(gameState, setGameState)}
              disabled={!gameState.canRevert || gameState.usedRevert}
              aria-label="Revert last move"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 7v6h6"></path>
                <path d="M21 17a9 9 0 0 0-9-9 9 9 0 0 0-6 2.3L3 13"></path>
              </svg>
            </button>
            
            {/* Reshuffle button */}
            <button 
              className={`reshuffle-button ${gameState.usedReshuffle ? 'disabled' : ''}`} 
              onClick={() => handleReshuffle(gameState, setGameState, cardsContainerRef)}
              disabled={gameState.usedReshuffle}
              aria-label="Reshuffle cards"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="16 3 21 3 21 8"></polyline>
                <line x1="4" y1="20" x2="21" y2="3"></line>
                <polyline points="21 16 21 21 16 21"></polyline>
                <line x1="15" y1="15" x2="21" y2="21"></line>
                <line x1="4" y1="4" x2="9" y2="9"></line>
              </svg>
            </button>
          </div>
        </div>
      </div>

      {gameState.isGameOver && (
        <div className="game-over-overlay">
          <div className="game-over-message">
            <h2>游戏结束</h2>
            <p>槽位已满，无法继续游戏</p>
            {gameState.revivalChances > 0 && (
              <button className="revive-button" onClick={() => {
                handleRevive(gameState, setGameState);
                // Also clear the invisible slot when reviving
                setInvisibleSlot([]);
                if (invisibleSlotsRef.current) {
                  invisibleSlotsRef.current.style.opacity = '0';
                }
              }}>
                买活 ({gameState.revivalChances})
              </button>
            )}
            <button className="restart-button" onClick={() => {
              setGameState(startNewGame());
              // Reset invisible slot
              setInvisibleSlot([]);
              if (invisibleSlotsRef.current) {
                invisibleSlotsRef.current.style.opacity = '0';
              }
            }}>重新开始</button>
          </div>
        </div>
      )}

      {gameState.isGameWon && (
        <div className="game-won-overlay">
          <div className="game-won-message">
            <div className="victory-animation">🎉</div>
            <h2>恭喜通关!</h2>
            <p>成功清除了所有卡片</p>
            <div className="victory-stars">
              <span className="star">⭐</span>
              <span className="star">⭐</span>
              <span className="star">⭐</span>
            </div>
            <button className="restart-button victory-button" onClick={() => {
              setGameState(startNewGame());
              // Reset invisible slot
              setInvisibleSlot([]);
              if (invisibleSlotsRef.current) {
                invisibleSlotsRef.current.style.opacity = '0';
              }
            }}>再来一局</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default GameBoard; 